import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../utils/constants';
import { toast } from 'sonner';
import FormWizard from './mobile/FormWizard';
import TouchInput, { TouchButton, TouchSelect } from './mobile/TouchInput';
import { useIsMobile } from '../hooks/useMediaQuery';
import '../pages/EmployeeFormPage.css';

/**
 * Employee Form with FormWizard (8 steps) - Mobile optimized
 * Falls back to original long form on desktop
 */
const EmployeeFormWizard = ({ location, onClose, accessCodeVerified = false }) => {
  const isMobile = useIsMobile();
  
  // Access code state
  const [codeVerified, setCodeVerified] = useState(accessCodeVerified);
  const [accessCode, setAccessCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    citizenship: 'Polska',
    desiredPosition: '',
    contractUntil: '',
    street: '',
    postalCode: '',
    city: '',
    country: 'Polska',
    phone: '',
    email: '',
    bankAccount: '',
    taxOffice: '',
    isFirstJob: false,
    isStudent: false,
    schoolName: '',
    fieldOfStudy: '',
    studentIdValidUntil: '',
    emergencyContact: '',
    emergencyPhone: '',
    // 11 oświadczeń
    informedAboutTerms: false,
    knowsContractForm: false,
    informedAboutSalary: false,
    knowsScheduleRules: false,
    commitsToRealization: false,
    knowsLocalRules: false,
    commitsToPropertyCare: false,
    infoAccurate: false,
    dataProcessingConsent: false,
    noObjections: false,
    hadQuestionOpportunity: false
  });

  const [studentAttachment, setStudentAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Verify access code
  const verifyAccessCode = async () => {
    if (!accessCode || accessCode.length !== 6) {
      toast.error('Wprowadź 6-cyfrowy kod');
      return;
    }

    setVerifyingCode(true);
    try {
      const response = await axios.get(
        `${API}/forms/verify-access-code?code=${accessCode}&location=${location}`
      );

      if (response.data.valid) {
        toast.success('✅ Kod prawidłowy!');
        setCodeVerified(true);
      } else {
        toast.error(response.data.message || 'Nieprawidłowy kod');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Błąd weryfikacji kodu');
    } finally {
      setVerifyingCode(false);
    }
  };

  // Submit form
  const handleSubmit = async (finalData) => {
    setSubmitting(true);
    try {
      const submitData = {
        location,
        personalData: {
          firstName: finalData.firstName,
          lastName: finalData.lastName,
          birthDate: finalData.birthDate,
          citizenship: finalData.citizenship,
          desiredPosition: finalData.desiredPosition,
          contractUntil: finalData.contractUntil
        },
        addressData: {
          street: finalData.street,
          postalCode: finalData.postalCode,
          city: finalData.city,
          country: finalData.country
        },
        contactData: {
          phone: finalData.phone,
          email: finalData.email
        },
        paymentData: {
          bankAccount: finalData.bankAccount
        },
        taxData: {
          taxOffice: finalData.taxOffice,
          isFirstJob: finalData.isFirstJob
        },
        educationData: {
          isStudent: finalData.isStudent,
          schoolName: finalData.schoolName || null,
          fieldOfStudy: finalData.fieldOfStudy || null,
          studentIdValidUntil: finalData.studentIdValidUntil || null,
          studentIdAttachment: studentAttachment || null
        },
        emergencyContact: {
          name: finalData.emergencyContact,
          phone: finalData.emergencyPhone
        },
        declarations: {
          informedAboutTerms: finalData.informedAboutTerms,
          knowsContractForm: finalData.knowsContractForm,
          informedAboutSalary: finalData.informedAboutSalary,
          knowsScheduleRules: finalData.knowsScheduleRules,
          commitsToRealization: finalData.commitsToRealization,
          knowsLocalRules: finalData.knowsLocalRules,
          commitsToPropertyCare: finalData.commitsToPropertyCare,
          infoAccurate: finalData.infoAccurate,
          dataProcessingConsent: finalData.dataProcessingConsent,
          noObjections: finalData.noObjections,
          hadQuestionOpportunity: finalData.hadQuestionOpportunity
        }
      };

      await axios.post(`${API}/forms`, submitData);
      toast.success('✅ Formularz wysłany pomyślnie!');
      onClose();
    } catch (error) {
      console.error('Submit error:', error);
      
      let errorMessage = 'Błąd wysyłania formularza';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        if (typeof detail === 'object' && Array.isArray(detail)) {
          errorMessage = detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Code verification screen
  if (!codeVerified) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '64px', marginBottom: '24px' }}>🔐</div>
        <h2>Kod dostępu wymagany</h2>
        <p style={{ marginBottom: '32px', color: 'var(--text-muted)' }}>
          Wprowadź 6-cyfrowy kod od managera
        </p>
        <TouchInput
          type="text"
          maxLength={6}
          value={accessCode}
          onChange={(e) => setAccessCode(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          fullWidth
          style={{ textAlign: 'center', fontSize: '24px', letterSpacing: '8px', fontFamily: 'monospace' }}
        />
        <TouchButton
          variant="primary"
          fullWidth
          loading={verifyingCode}
          onClick={verifyAccessCode}
          style={{ marginTop: '16px' }}
        >
          🔓 Weryfikuj kod
        </TouchButton>
      </div>
    );
  }

  // Define wizard steps
  const steps = [
    {
      title: '1. Dane osobowe',
      description: 'Podstawowe informacje o Tobie',
      component: ({ formData: data, updateFormData }) => (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
          <TouchInput label="Imię" required value={data.firstName || ''} onChange={(e) => updateFormData({ firstName: e.target.value })} />
          <TouchInput label="Nazwisko" required value={data.lastName || ''} onChange={(e) => updateFormData({ lastName: e.target.value })} />
          <TouchInput label="Data urodzenia" type="date" required value={data.birthDate || ''} onChange={(e) => updateFormData({ birthDate: e.target.value })} />
          <TouchInput label="Obywatelstwo" value={data.citizenship || 'Polska'} onChange={(e) => updateFormData({ citizenship: e.target.value })} />
          <TouchInput label="Jakie stanowisko" required fullWidth value={data.desiredPosition || ''} onChange={(e) => updateFormData({ desiredPosition: e.target.value })} />
          <TouchInput label="Do kiedy umowa" type="date" required fullWidth value={data.contractUntil || ''} onChange={(e) => updateFormData({ contractUntil: e.target.value })} />
        </div>
      ),
      validate: async (data) => {
        if (!data.firstName || !data.lastName || !data.birthDate || !data.desiredPosition || !data.contractUntil) {
          toast.error('Wypełnij wszystkie wymagane pola');
          return false;
        }
        return true;
      }
    },
    {
      title: '2. Adres zamieszkania',
      description: 'Gdzie mieszkasz?',
      component: ({ formData: data, updateFormData }) => (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
          <TouchInput label="Ulica i numer" required fullWidth value={data.street || ''} onChange={(e) => updateFormData({ street: e.target.value })} />
          <TouchInput label="Kod pocztowy" required value={data.postalCode || ''} onChange={(e) => updateFormData({ postalCode: e.target.value })} />
          <TouchInput label="Miasto" required value={data.city || ''} onChange={(e) => updateFormData({ city: e.target.value })} />
          <TouchInput label="Kraj" value={data.country || 'Polska'} onChange={(e) => updateFormData({ country: e.target.value })} />
        </div>
      ),
      validate: async (data) => {
        if (!data.street || !data.postalCode || !data.city) {
          toast.error('Wypełnij dane adresowe');
          return false;
        }
        return true;
      }
    },
    {
      title: '3. Kontakt',
      description: 'Jak się z Tobą skontaktować?',
      component: ({ formData: data, updateFormData }) => (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
          <TouchInput label="Telefon" type="tel" required value={data.phone || ''} onChange={(e) => updateFormData({ phone: e.target.value })} />
          <TouchInput label="Email" type="email" required value={data.email || ''} onChange={(e) => updateFormData({ email: e.target.value })} />
        </div>
      ),
      validate: async (data) => {
        if (!data.phone || !data.email) {
          toast.error('Podaj telefon i email');
          return false;
        }
        return true;
      }
    },
    {
      title: '4. Dane do wypłaty',
      description: 'Rachunek bankowy',
      component: ({ formData: data, updateFormData }) => (
        <div>
          <TouchInput label="Numer konta bankowego" required fullWidth value={data.bankAccount || ''} onChange={(e) => updateFormData({ bankAccount: e.target.value })} placeholder="PL..." />
        </div>
      ),
      validate: async (data) => {
        if (!data.bankAccount) {
          toast.error('Podaj numer konta');
          return false;
        }
        return true;
      }
    },
    {
      title: '5. Dane podatkowe',
      description: 'Informacje dla US',
      component: ({ formData: data, updateFormData }) => (
        <div style={{ display: 'grid', gap: '16px' }}>
          <TouchInput label="Urząd skarbowy" required fullWidth value={data.taxOffice || ''} onChange={(e) => updateFormData({ taxOffice: e.target.value })} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '15px' }}>
            <input type="checkbox" checked={data.isFirstJob || false} onChange={(e) => updateFormData({ isFirstJob: e.target.checked })} />
            To jest moja pierwsza praca
          </label>
        </div>
      )
    },
    {
      title: '6. Status studenta',
      description: 'Czy jesteś studentem?',
      component: ({ formData: data, updateFormData }) => (
        <div style={{ display: 'grid', gap: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '15px' }}>
            <input type="checkbox" checked={data.isStudent || false} onChange={(e) => updateFormData({ isStudent: e.target.checked })} />
            Jestem studentem
          </label>
          {data.isStudent && (
            <>
              <TouchInput label="Nazwa uczelni" value={data.schoolName || ''} onChange={(e) => updateFormData({ schoolName: e.target.value })} />
              <TouchInput label="Kierunek studiów" value={data.fieldOfStudy || ''} onChange={(e) => updateFormData({ fieldOfStudy: e.target.value })} />
              <TouchInput label="Ważność legitymacji" type="date" value={data.studentIdValidUntil || ''} onChange={(e) => updateFormData({ studentIdValidUntil: e.target.value })} />
            </>
          )}
        </div>
      )
    },
    {
      title: '7. Kontakt awaryjny',
      description: 'W razie nagłej potrzeby',
      component: ({ formData: data, updateFormData }) => (
        <div style={{ display: 'grid', gap: '16px' }}>
          <TouchInput label="Imię i nazwisko" required fullWidth value={data.emergencyContact || ''} onChange={(e) => updateFormData({ emergencyContact: e.target.value })} />
          <TouchInput label="Telefon" type="tel" required fullWidth value={data.emergencyPhone || ''} onChange={(e) => updateFormData({ emergencyPhone: e.target.value })} />
        </div>
      ),
      validate: async (data) => {
        if (!data.emergencyContact || !data.emergencyPhone) {
          toast.error('Podaj kontakt awaryjny');
          return false;
        }
        return true;
      }
    },
    {
      title: '8. Oświadczenia',
      description: 'Zaznacz wszystkie wymagane pola',
      component: ({ formData: data, updateFormData }) => (
        <div style={{ display: 'grid', gap: '12px', fontSize: '14px' }}>
          {[
            { key: 'informedAboutTerms', label: 'Oświadczam, że zostałem/am poinformowany/a o warunkach umowy' },
            { key: 'knowsContractForm', label: 'Znam formę kontraktu (umowa zlecenie)' },
            { key: 'informedAboutSalary', label: 'Zostałem/am poinformowany/a o wynagrodzeniu' },
            { key: 'knowsScheduleRules', label: 'Znam zasady dotyczące grafiku' },
            { key: 'commitsToRealization', label: 'Zobowiązuję się do realizacji powierzonych zadań' },
            { key: 'knowsLocalRules', label: 'Znam zasady obowiązujące w lokalu' },
            { key: 'commitsToPropertyCare', label: 'Zobowiązuję się do dbałości o mienie' },
            { key: 'infoAccurate', label: 'Potwierdzam, że informacje są prawidłowe' },
            { key: 'dataProcessingConsent', label: 'Wyrażam zgodę na przetwarzanie danych osobowych' },
            { key: 'noObjections', label: 'Nie mam zastrzeżeń do warunków' },
            { key: 'hadQuestionOpportunity', label: 'Miałem/am możliwość zadania pytań' }
          ].map(({ key, label }) => (
            <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
              <input type="checkbox" required checked={data[key] || false} onChange={(e) => updateFormData({ [key]: e.target.checked })} />
              <span>{label}</span>
            </label>
          ))}
        </div>
      ),
      validate: async (data) => {
        const required = ['informedAboutTerms', 'knowsContractForm', 'informedAboutSalary', 'knowsScheduleRules', 'commitsToRealization', 'knowsLocalRules', 'commitsToPropertyCare', 'infoAccurate', 'dataProcessingConsent', 'noObjections', 'hadQuestionOpportunity'];
        const allChecked = required.every(key => data[key]);
        if (!allChecked) {
          toast.error('Zaznacz wszystkie wymagane oświadczenia');
          return false;
        }
        return true;
      }
    }
  ];

  return (
    <FormWizard
      steps={steps}
      onComplete={handleSubmit}
      onCancel={onClose}
    />
  );
};

export default EmployeeFormWizard;
